import { FC, useEffect, useState } from 'react';
import { Avatar, Box, Typography } from '@mui/material';
import { getHighestPriorityRole } from 'src/utils/roles.utils';
import { capitalizeFirstLetter, stringToColor } from 'src/utils/text.utils';
import { dataStorageKeywords } from 'src/utils/properties.utils';
import { getEncryptedLocalStorageData } from 'src/utils/dataStorageManagement.utils';
import { useUserProfile } from 'src/context/UserProfileContext';

interface AvatarProps {
  name: string;
  role: string;
  src?: string;
  size?: number;
}

const AvatarComponent: FC<AvatarProps> = ({ name, role, src, size = 50 }) => {
  const avatarColor = stringToColor(name);

  return (
    <Box display="flex" alignItems="center">
      <Avatar
        src={src}
        sx={{
          bgcolor: avatarColor,
          width: '40px',
          height: '40px',
          fontSize: size / 2,
          marginRight: 1,
        }}
      >
        {name.charAt(0)}
      </Avatar>
      <Box display="flex" flexDirection="column" alignItems="flex-start">
        <Typography
          fontFamily="Outfit"
          fontWeight="400"
          fontSize="14px"
          color="var(--primary-navy)"
        >
          {name}
        </Typography>
        <Typography
          fontFamily="Outfit"
          fontWeight="400"
          fontSize="12px"
          color="var(--instructions)"
        >
          {capitalizeFirstLetter(role)}
        </Typography>
      </Box>
    </Box>
  );
};

const UserProfile: FC = () => {
  const { userProfile } = useUserProfile();
  const [fullName, setFullName] = useState<string>('User');
  const [userRole, setUserRole] = useState<string>('');

  const truncateName = (name: string, maxLength: number) => {
    return name.length > maxLength
      ? `${name.substring(0, maxLength)}...`
      : name;
  };

  const fetchUserData = async () => {
    const fullNameData =
      (await getEncryptedLocalStorageData(dataStorageKeywords.FULL_NAME)) ||
      'User';
    const rolesData = JSON.parse(
      (await getEncryptedLocalStorageData(dataStorageKeywords.ROLES)) || '[]'
    ) as string[];

    setFullName(fullNameData);
    setUserRole(getHighestPriorityRole(rolesData));
  };

  useEffect(() => {
    fetchUserData();
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === dataStorageKeywords.FULL_NAME) {
        fetchUserData();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return (
    <AvatarComponent
      name={truncateName(fullName, 18)}
      role={capitalizeFirstLetter(userRole)}
      src={userProfile?.avatarUrl}
    />
  );
};

export default UserProfile;
